// Bounding-box math shared by the SPA and the weather-proxy Lambda.
//
// The trip map queries weather for the bounding box of the whole route, and
// nothing about a route bounds how big that box gets: one mis-geocoded
// waypoint (geocodeName takes the geocoder's top hit verbatim) can stretch it
// across the planet. A hemisphere-wide METAR query returns thousands of
// stations -- megabytes through the proxy, a marker per station in the
// browser, and a pointless load on a free government API.
//
// So there is a cap, enforced in BOTH places:
// - the SPA skips the bbox-driven fetches and explains why (TripRouteMap),
// - the proxy rejects an oversize bbox with a 400 (its Function URL is public
//   and unauthenticated, so the client-side check protects nothing on its own).
//
// This module holds no route knowledge -- the Lambda imports it and must not
// pull in TripRoute.mjs.

// Roughly the US eastern seaboard: a few hundred METARs, which is the most
// that's useful on one map anyway. Anything larger is either a mis-geocode or
// a route too long for point observations to mean anything at map zoom.
//
// Longitude gets the wider allowance because a degree of longitude shrinks
// with latitude, so an east-west box covers less ground than its span suggests.
export const MAX_WEATHER_LAT_SPAN_DEG = 12;
export const MAX_WEATHER_LON_SPAN_DEG = 24;

// The ceiling on ONE viewport-driven query, and a different kind of limit from
// the pair above.
//
// Those two are a REFUSAL: a box that big is a mis-geocode, the layers go quiet
// and the map explains why. This is a CLAMP: the view is legitimate, the user
// has simply zoomed out past the point where every station in sight can be
// fetched, so the query shrinks around the camera and the map says that distant
// stations were left out. Refusing here would blank a view that is perfectly
// reasonable to be looking at.
//
// Sized to sit just above the largest legitimate view rather than to bite in
// normal use. The Salish page's widest area ("Entire region") is 3.2 x 3.4
// degrees, 4.0 x 4.2 once padded, and must not clamp -- it is the page's
// default and its whole purpose is the overview. The cached region padded is
// 6.5 x 5.1, so zooming out past the region does clamp. That leaves this a
// ceiling rather than a routine constraint, which is the intent: it exists so
// one request's size is bounded by a number in this file instead of by however
// large REGION_BOUNDS happens to grow.
//
// Longitude gets the wider allowance for the same reason as above.
export const MAX_QUERY_LAT_SPAN_DEG = 4.5;
export const MAX_QUERY_LON_SPAN_DEG = 5.5;

// `bounds` shrunk around its own centre until it is within the given spans.
//
// Returns the SAME REFERENCE when nothing needed shrinking, so a caller can
// tell "clamped" from "not clamped" by identity rather than by re-measuring --
// which is what decides whether the map says anything about it.
//
// Only ever shrinks. A box smaller than the cap is returned untouched rather
// than grown to fill it: the caller asked about what it can see.
export function clampBoundsSpan(
  bounds,
  maxLatSpan = MAX_QUERY_LAT_SPAN_DEG,
  maxLonSpan = MAX_QUERY_LON_SPAN_DEG
) {
  const span = boundsSpan(bounds);
  if (!span) return bounds;
  if (span.latSpan <= maxLatSpan && span.lonSpan <= maxLonSpan) return bounds;

  const [[minLon, minLat], [maxLon, maxLat]] = bounds;
  const centreLon = (minLon + maxLon) / 2;
  const centreLat = (minLat + maxLat) / 2;
  const halfLon = Math.min(span.lonSpan, maxLonSpan) / 2;
  const halfLat = Math.min(span.latSpan, maxLatSpan) / 2;
  return [
    [centreLon - halfLon, centreLat - halfLat],
    [centreLon + halfLon, centreLat + halfLat],
  ];
}

// Great-circle distance in nautical miles (haversine; Earth radius in nm).
export const EARTH_RADIUS_NM = 3440.065;

export function haversineNm(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_NM * Math.asin(Math.sqrt(h));
}

// Point `distanceNm` along `bearingDeg` (true) from an origin, on a sphere.
// The trip map uses it to probe around a waypoint whose exact coordinate
// misses the thing being looked up -- a harbor sits on land by definition, so
// "which marine zone is this waypoint in" has no answer at the waypoint
// itself, only a couple of miles offshore.
export function destinationPoint(origin, bearingDeg, distanceNm) {
  const { lat, lon } = origin || {};
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (!Number.isFinite(bearingDeg) || !Number.isFinite(distanceNm)) return null;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;
  const angular = distanceNm / EARTH_RADIUS_NM;
  const lat1 = toRad(lat);
  const lon1 = toRad(lon);
  const bearing = toRad(bearingDeg);
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(bearing)
  );
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(angular) * Math.cos(lat1),
      Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2)
    );
  // Wrap into [-180, 180] -- a ring near the antimeridian would otherwise
  // hand callers longitudes an API rejects as out of range.
  return { lat: toDeg(lat2), lon: (((toDeg(lon2) + 540) % 360) - 180) };
}

// The great-circle midpoint of two points. Its caller is the other half of
// destinationPoint's rule: a harbor is on land by definition, and the WATER is
// between the harbors -- so the middle of a leg is a far better guess at "which
// marine zone does this route cross" than any bearing off either end, and it is
// a guess about the route rather than about the compass.
//
// Spherical rather than an average of the two coordinates, which would wander
// on a long leg and break outright across the antimeridian.
export function midpoint(a, b) {
  if (!Number.isFinite(a?.lat) || !Number.isFinite(a?.lon)) return null;
  if (!Number.isFinite(b?.lat) || !Number.isFinite(b?.lon)) return null;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;
  const lat1 = toRad(a.lat);
  const lon1 = toRad(a.lon);
  const lat2 = toRad(b.lat);
  const dLon = toRad(b.lon - a.lon);
  const bx = Math.cos(lat2) * Math.cos(dLon);
  const by = Math.cos(lat2) * Math.sin(dLon);
  const lat3 = Math.atan2(
    Math.sin(lat1) + Math.sin(lat2),
    Math.sqrt((Math.cos(lat1) + bx) ** 2 + by ** 2)
  );
  const lon3 = lon1 + Math.atan2(by, Math.cos(lat1) + bx);
  return { lat: toDeg(lat3), lon: (((toDeg(lon3) + 540) % 360) - 180) };
}

// `count` evenly spaced points on a circle of `radiusNm` around `origin`, the
// first at `startBearingDeg` and the rest clockwise from it.
export function probeRing(origin, radiusNm, count = 8, startBearingDeg = 0) {
  if (!Number.isInteger(count) || count < 1) return [];
  const step = 360 / count;
  return Array.from({ length: count }, (_, index) =>
    destinationPoint(origin, (startBearingDeg + index * step) % 360, radiusNm)
  ).filter(Boolean);
}

// Degrees covered by [[minLon, minLat], [maxLon, maxLat]], or null.
export function boundsSpan(bounds) {
  if (!Array.isArray(bounds) || bounds.length !== 2) return null;
  const [[minLon, minLat], [maxLon, maxLat]] = bounds;
  if (![minLon, minLat, maxLon, maxLat].every((v) => Number.isFinite(v))) return null;
  return { latSpan: maxLat - minLat, lonSpan: maxLon - minLon };
}

// Smallest box containing every valid box in the list, or null when none.
// Invalid entries (null, malformed) are skipped, not treated as empty boxes.
export function unionBounds(boundsList) {
  let union = null;
  for (const bounds of boundsList || []) {
    if (!boundsSpan(bounds)) continue;
    const [[minLon, minLat], [maxLon, maxLat]] = bounds;
    if (!union) {
      union = [[minLon, minLat], [maxLon, maxLat]];
    } else {
      union = [
        [Math.min(union[0][0], minLon), Math.min(union[0][1], minLat)],
        [Math.max(union[1][0], maxLon), Math.max(union[1][1], maxLat)],
      ];
    }
  }
  return union;
}

// Smallest box containing every valid { lat, lon } in the list, in maplibre's
// [[minLon, minLat], [maxLon, maxLat]] corner order, or null when none of them
// is usable. Points with a missing or non-finite coordinate are skipped.
//
// TripRoute has routeBounds for the same job, but it only speaks routes; this
// takes bare points, which is what the gazetteer map (and anything else framing
// a loose collection of positions) has.
//
// A single point yields a zero-span box. That is deliberate and correct for
// fitBounds, which honors its maxZoom rather than zooming to infinity.
export function boundsForPoints(points) {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  let found = false;
  for (const point of points || []) {
    const lat = point?.lat;
    const lon = point?.lon;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    found = true;
    if (lon < minLon) minLon = lon;
    if (lat < minLat) minLat = lat;
    if (lon > maxLon) maxLon = lon;
    if (lat > maxLat) maxLat = lat;
  }
  return found ? [[minLon, minLat], [maxLon, maxLat]] : null;
}

// True when two boxes overlap at all (edges count as touching).
//
// The weather cache's filter, at both ends: the poller keeps a zone, alert or
// advisory whose footprint reaches the cached region, and the read Lambda hands
// back the ones that reach the caller's box. Intersection, not containment --
// a marine zone is much larger than the route drawn across it, and requiring
// containment either way would drop exactly the zone the route is IN.
//
// A null or unreadable box does not intersect anything. Callers gate on having
// a box first; treating garbage as "matches everything" would return the whole
// table.
export function boundsIntersect(a, b) {
  if (!boundsSpan(a) || !boundsSpan(b)) return false;
  const [[aMinLon, aMinLat], [aMaxLon, aMaxLat]] = a;
  const [[bMinLon, bMinLat], [bMaxLon, bMaxLat]] = b;
  return aMinLon <= bMaxLon && aMaxLon >= bMinLon && aMinLat <= bMaxLat && aMaxLat >= bMinLat;
}

// The features of a GeoJSON collection whose `bbox` overlaps `bounds`.
//
// This is how the weather card is culled to the map's view: the map draws
// everything the query box answered, the card lists the part of it that is on
// screen. Overlap, not containment, for the reason boundsIntersect gives -- a
// marine zone is much larger than the view drawn over it.
//
// A feature with NO bbox is KEPT. A geometry-less alert (NWS names its zones by
// URL) or a land forecast zone (a whole county, deliberately never drawn)
// cannot be placed on the screen at all, and a real gale warning dropped to
// tidy the card is the wrong way round; wx-read's readAlerts makes the same
// call for the same reason. Culling those is the caller's job, if it has a
// better fact to cull on (the card drops land zones when no anchor is listed).
//
// No collection answers null, so an unfetched layer stays "unfetched" rather
// than becoming an empty answer. No bounds answers the collection unchanged:
// nothing to cull against means nothing culled, which is what a map that has
// not reported a view yet wants.
export function featuresInBounds(collection, bounds) {
  if (!collection) return null;
  if (!boundsSpan(bounds)) return collection;
  const features = (collection.features || []).filter(
    (feature) => !feature?.bbox || boundsIntersect(feature.bbox, bounds)
  );
  return { ...collection, features };
}

// True when the whole of `inner` is inside `outer` (edges count).
//
// Containment where boundsIntersect is overlap, and the two answer different
// questions: overlap decides whether a zone belongs in a box's answer, while
// this decides whether a box's answer is already IN HAND. The map's weather
// skips a fetch on it -- a view inside the box last fetched for needs nothing,
// because every bbox layer answers "everything in this box" rather than "the N
// nearest its centre". A layer that reintroduces a cap breaks that and must not
// be skipped on this.
//
// A null or unreadable box neither covers nor is covered. Callers gate on
// having a box first; treating garbage as covering everything would skip every
// fetch there is.
export function boundsCover(outer, inner) {
  if (!boundsSpan(outer) || !boundsSpan(inner)) return false;
  const [[oMinLon, oMinLat], [oMaxLon, oMaxLat]] = outer;
  const [[iMinLon, iMinLat], [iMaxLon, iMaxLat]] = inner;
  return iMinLon >= oMinLon && iMaxLon <= oMaxLon && iMinLat >= oMinLat && iMaxLat <= oMaxLat;
}

// `bounds` grown by a fraction of its own span on every side, or null.
//
// Proportional rather than a fixed number of degrees (padBounds' job), because
// its caller is asking for slack measured in gestures: a margin worth a few
// pans at the zoom the user is actually at, which is a fraction of the view and
// not a distance.
export function growBounds(bounds, fraction) {
  if (!boundsSpan(bounds) || !Number.isFinite(fraction)) return null;
  const [[minLon, minLat], [maxLon, maxLat]] = bounds;
  const lonMargin = (maxLon - minLon) * fraction;
  const latMargin = (maxLat - minLat) * fraction;
  const fix = (value) => Number(value.toFixed(6));
  return [
    [fix(minLon - lonMargin), fix(minLat - latMargin)],
    [fix(maxLon + lonMargin), fix(maxLat + latMargin)],
  ];
}

// True when the box covers more ground than a weather query should. A null or
// unreadable box is NOT "too large" -- callers gate on the box existing first,
// and treating garbage as oversize would show the wrong explanation.
export function boundsTooLarge(bounds) {
  const span = boundsSpan(bounds);
  if (!span) return false;
  return span.latSpan > MAX_WEATHER_LAT_SPAN_DEG || span.lonSpan > MAX_WEATHER_LON_SPAN_DEG;
}

// aviationweather.gov's bbox parameter, "minLat,minLon,maxLat,maxLon", parsed
// into the [[minLon, minLat], [maxLon, maxLat]] corner order the rest of the
// app uses. Null when it isn't four in-range numbers in increasing order --
// this is what the proxy validates untrusted query strings with, so it has to
// reject rather than coerce.
export function parseAwcBbox(text) {
  if (typeof text !== 'string') return null;
  const parts = text.split(',');
  if (parts.length !== 4) return null;

  const numbers = parts.map((part) => {
    const trimmed = part.trim();
    // Number('') is 0 and Number(' 1 ') is 1; require an actual numeric literal.
    if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return NaN;
    return Number(trimmed);
  });
  if (!numbers.every((value) => Number.isFinite(value))) return null;

  const [minLat, minLon, maxLat, maxLon] = numbers;
  if (minLat < -90 || maxLat > 90 || minLon < -180 || maxLon > 180) return null;
  if (minLat >= maxLat || minLon >= maxLon) return null;

  return [[minLon, minLat], [maxLon, maxLat]];
}
